import { useMemo, useState } from "react";
import TableComponent from "../../components/common/TableComponent";
import PaginationComponent from "../../components/PaginationComponent";
import Loading from "../../components/common/LoaderCommon";
import { Search, X } from "lucide-react";

import {
  useDownloadSalesReturnReport,
  useInfiniteGetSalesReturnReport,
} from "../../service/reportsService";
import DownloadButton from "./DownloadFile";
import { ReusableCombobox } from "../../utils/components/ui/combobox";
import { useGetAllUsers } from "../../service/userService";

type Props = {};

function SalesReturnReport({}: Props) {
  const [searchKey, setSearchKey] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [selectedCustomerEmail, setSelectedCustomerEmail] = useState("");
  const [userComboboxOpen, setUserComboboxOpen] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  const [currentPage, setCurrentPage] = useState(0);

  const downloadMutation = useDownloadSalesReturnReport();

  const { data, fetchNextPage, isLoading } = useInfiniteGetSalesReturnReport({
    pageSize: 10,
    pageNo: 0,
    salesReturnNumber: searchKey,
    startDate: startDate,
    endDate: endDate,
    email: selectedCustomerEmail,
  });

  const { data: allUsersData } = useGetAllUsers({
    pageSize: 10,
    pageNo: 0,
    userName: userSearch,
  });

  const totalPages = Math.ceil((Number(data?.pages[0]?.totalPages) || 0) / 10);

  const salesReturnData = data?.pages[0]?.salesReturnResponse || [];

  const purchaseColumns = useMemo(
    () => [
      { header: "Invoice Number", accessor: "invoiceNumber" },
      { header: "Customer Name", accessor: "customerName" },
      { header: "Sales Date", accessor: "salesReturnDate" },
      { header: "Total Amount", accessor: "amountRefunded" },
    ],
    [],
  );

  const userOptions = allUsersData?.userDetailsResponse;

  const isFilterActive =
    !!searchKey || !!startDate || !!endDate || !!selectedCustomerEmail;

  const handleResetFilters = () => {
    setCurrentPage(0);
    setSelectedCustomerEmail("");
    setStartDate("");
    setEndDate("");
    setSearchKey("");
  };

  return (
    <div className="space-y-4">
      {/* Filter card */}
      <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] p-5">
        <div className="flex justify-between items-end flex-wrap gap-4">
          <div className="flex flex-col gap-4">
            {/* Row 1: User combobox + search */}
            <div className="flex gap-4 flex-wrap items-end">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  User
                </label>
                <div className="min-w-[280px]">
                  <ReusableCombobox
                    options={
                      userOptions?.map((user) => ({
                        value: user.email,
                        label: user.username,
                      })) || []
                    }
                    value={selectedCustomerEmail}
                    onValueChange={(value) => {
                      setSelectedCustomerEmail(value as string);
                    }}
                    open={userComboboxOpen}
                    setOpen={setUserComboboxOpen}
                    placeholder="Select User"
                    searchPlaceholder="Search user..."
                    emptyText="No users found."
                    className="bg-[#F5F8FD] border-[#E5ECF8] rounded-[12px] border font-normal h-11"
                    onInputChange={(val) => setUserSearch(val)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#52627C]" />
                  <input
                    type="text"
                    placeholder="Search sales return number"
                    onChange={(e) => setSearchKey(e.target.value)}
                    className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] pl-11 pr-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF] w-[280px]"
                  />
                </div>
              </div>
            </div>

            {/* Row 2: Date pickers + reset */}
            <div className="flex gap-4 flex-wrap items-end">
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  Start Date
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-[#52627C] mb-1.5 block">
                  End Date
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] bg-[#F5F8FD] px-4 text-sm text-[#23324D] outline-none transition focus:border-[#BFD1FF]"
                />
              </div>
              {isFilterActive && (
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="h-11 rounded-[12px] border border-[#E5ECF8] px-5 text-[#52627C] font-medium text-sm flex items-center gap-2 hover:bg-[#F5F8FD] transition-colors self-end"
                >
                  <X className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>

          <DownloadButton
            endDate={endDate}
            startDate={startDate}
            mutation={downloadMutation}
          />
        </div>
      </div>

      {/* Table card */}
      {isLoading ? (
        <Loading />
      ) : (
        <div className="rounded-[26px] border border-[#E8EEF9] bg-white shadow-[0_16px_40px_rgba(26,94,249,0.08)] overflow-hidden">
          <div className="flex justify-between items-center border-b border-[#E8EEF9] py-3 px-6">
            <p className="text-sm text-[#52627C]">
              <span className="font-semibold text-[#21314A]">
                {data?.pages[0]?.totalElements}
              </span>{" "}
              Sales returns found
            </p>
            <PaginationComponent
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => setCurrentPage(page)}
              fetchNextPage={fetchNextPage}
              fetchPreviousPage={() => {
                if (currentPage > 1) {
                  setCurrentPage(currentPage - 1);
                }
              }}
            />
          </div>
          <TableComponent
            caption="A list of your recent invoices."
            columns={purchaseColumns}
            data={salesReturnData || []}
          />
        </div>
      )}
    </div>
  );
}

export default SalesReturnReport;
